import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { api, ApiError, body } from "./api.js";

type Role = "admin" | "user";
type Scope = "artifacts:read" | "artifacts:write" | "comments:write";
type Permission = "read" | "write";
type ArtifactKind = "freeform" | "designed";

interface Session {
    actor: { userId: string; role: Role };
    user: { id: string; name: string; email: string; role?: string };
}

interface ArtifactSummary {
    id: string;
    title: string;
    ownerId: string;
    currentVersion: number;
    updatedAt: string;
    permission: Permission;
    kind: ArtifactKind;
}

interface Artifact extends ArtifactSummary {
    html: string;
    etag: string;
}

interface ManagedUser {
    id: string;
    name: string;
    email: string;
    role: Role;
    banned: boolean;
    createdAt: string;
    mcpEnabled: boolean;
    mcpScopes: Scope[];
}

interface OAuthClient {
    clientId: string;
    name: string;
    redirectUris: string[];
    scopes: Scope[];
    disabled: boolean;
    createdAt: string;
}

interface AuditEvent {
    id: string;
    actorId: string | null;
    action: string;
    targetType: string;
    targetId: string | null;
    outcome: string;
    createdAt: string;
}

export function App() {
    const [session, setSession] = useState<Session | null>();
    const [error, setError] = useState("");

    const refreshSession = useCallback(async () => {
        try {
            setSession(await api<Session>("/shell-api/session"));
        } catch (requestError) {
            if (requestError instanceof ApiError && requestError.status === 401) setSession(null);
            else setError(message(requestError));
        }
    }, []);

    useEffect(() => void refreshSession(), [refreshSession]);

    if (session === undefined) return <main className="center-card"><p>Loading secure session…</p></main>;
    if (!session) return <Login onAuthenticated={refreshSession} error={error} />;
    if (window.location.pathname === "/consent") return <Consent />;
    const viewing = /^\/view\/([0-9a-f-]{36})$/i.exec(window.location.pathname)?.[1];
    if (viewing) return <Viewer artifactId={viewing} />;
    return <Workspace session={session} onError={setError} error={error} />;
}

/**
 * Read-only, full-bleed view of one artifact, reachable at a stable `/view/<artifact-id>`.
 *
 * The stable part is this page, not the render URL: a render token is single-use and lives about
 * a minute, so it is fetched here on every load and never appears in the address bar. Anyone
 * following the link still has to be signed in and still has to hold an ACL grant -- the link
 * itself grants nothing, which is the property the ticket insists on.
 */
export function Viewer({ artifactId }: { artifactId: string }) {
    const [frameUrl, setFrameUrl] = useState("");
    const [title, setTitle] = useState("");
    const [failure, setFailure] = useState("");
    const [designed, setDesigned] = useState(false);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const artifact = await api<Artifact>(`/shell-api/artifacts/${artifactId}`);
                if (cancelled) return;
                setTitle(artifact.title);
                document.title = `${artifact.title} · HeyRobin Artifacts`;
                // Reaching this component with a designed artifact means the server route fell
                // through -- it renders those itself. Asking for a render token here would put the
                // stored document model on screen as raw JSON, so say so instead of showing it.
                if (artifact.kind === "designed") {
                    setDesigned(true);
                    return;
                }
                const render = await api<{ token: string }>(
                    `/shell-api/artifacts/${artifactId}/render-token`,
                    { method: "POST" },
                );
                if (cancelled) return;
                setFrameUrl(`/render/${encodeURIComponent(artifactId)}?token=${encodeURIComponent(render.token)}`);
            } catch (requestError) {
                if (!cancelled) setFailure(message(requestError));
            }
        })();
        return () => { cancelled = true; };
    }, [artifactId]);

    if (designed) {
        return <main className="center-card">
            <section className="panel login">
                <h1>{title}</h1>
                <p className="muted">This document is rendered on the server. Reload to read it.</p>
                <button className="primary" onClick={() => window.location.reload()}>Reload</button>
                <a className="link" href="/">Back to the workspace</a>
            </section>
        </main>;
    }

    if (failure) {
        return <main className="center-card">
            <section className="panel login">
                <h1>Not available</h1>
                <p className="error" role="alert">{failure}</p>
                <p className="muted">You need an access grant for this artifact.</p>
                <a className="primary" href="/">Back to the workspace</a>
            </section>
        </main>;
    }

    return <div className="viewer">
        <header className="viewer-bar">
            <a href="/" title="Back to the workspace">&larr;</a>
            <strong>{title || "Loading…"}</strong>
            <a href={`/artifacts/${artifactId}`}>Edit</a>
        </header>
        {frameUrl
            ? <iframe title={title || "Artifact"} src={frameUrl} sandbox="allow-scripts allow-forms" referrerPolicy="no-referrer" />
            : <p className="muted viewer-status">Issuing a one-time render token…</p>}
    </div>;
}

function Login({ onAuthenticated, error }: { onAuthenticated: () => Promise<void>; error: string }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [localError, setLocalError] = useState(error);

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        setBusy(true);
        setLocalError("");
        try {
            await api("/api/auth/sign-in/email", {
                method: "POST",
                ...body({ email, password, rememberMe: false }),
            });
            if (window.location.search.includes("client_id=") || window.location.search.includes("sig=")) {
                window.location.assign(`/api/auth/oauth2/authorize${window.location.search}`);
                return;
            }
            // /view/<id> renders a designed document server side, and that route can only do it
            // when the request carries the session cookie. A visitor arriving signed out gets the
            // SPA instead, and signing in here never repeats that request -- so without this the
            // client-side viewer takes over and shows the stored document model as what it
            // literally is: raw JSON. Reloading hands the URL back to the server.
            if (window.location.pathname.startsWith("/view/")) {
                window.location.reload();
                return;
            }
            await onAuthenticated();
        } catch (requestError) {
            setLocalError(message(requestError));
        } finally {
            setBusy(false);
        }
    };

    return <main className="center-card">
        <form className="panel login" onSubmit={submit}>
            <div className="brand">HeyRobin <span>Artifacts</span></div>
            <h1>Sign in</h1>
            <p className="muted">Independent, secured artifact workspace</p>
            <label>Email<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
            <label>Password<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
            {localError && <p className="error" role="alert">{localError}</p>}
            <button className="primary" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
        </form>
    </main>;
}

function Consent() {
    const parameters = useMemo(() => new URLSearchParams(window.location.search), []);
    const scopes = parameters.get("scope")?.split(" ").filter(Boolean) ?? [];
    const [error, setError] = useState("");

    const decide = async (accept: boolean) => {
        try {
            const result = await api<{ redirect?: boolean; url?: string; redirect_uri?: string }>(
                "/api/auth/oauth2/consent",
                { method: "POST", ...body({ accept, oauth_query: window.location.search.slice(1) }) },
            );
            const target = result.url ?? result.redirect_uri;
            if (!target) throw new Error("Authorization server returned no redirect");
            window.location.assign(target);
        } catch (requestError) {
            setError(message(requestError));
        }
    };

    return <main className="center-card">
        <section className="panel login">
            <h1>Authorize MCP client</h1>
            <p><strong>{parameters.get("client_id") ?? "Unknown client"}</strong> requests:</p>
            <ul>{scopes.map((scope) => <li key={scope}><code>{scope}</code></li>)}</ul>
            {error && <p className="error" role="alert">{error}</p>}
            <div className="actions">
                <button className="secondary" onClick={() => void decide(false)}>Deny</button>
                <button className="primary" onClick={() => void decide(true)}>Allow</button>
            </div>
        </section>
    </main>;
}

function Workspace({ session, error, onError }: {
    session: Session;
    error: string;
    onError: (value: string) => void;
}) {
    const [section, setSection] = useState<"artifacts" | "admin">("artifacts");
    const logout = async () => {
        await api("/api/auth/sign-out", { method: "POST" });
        window.location.assign("/login");
    };
    return <div className="layout">
        <aside>
            <div className="brand">HeyRobin <span>Artifacts</span></div>
            <nav>
                <a href="/" className={section === "artifacts" ? "active" : ""}
                    aria-current={section === "artifacts" && window.location.pathname === "/" ? "page" : undefined}>Artifacts</a>
                {session.actor.role === "admin" &&
                    <button className={section === "admin" ? "active" : ""} onClick={() => setSection("admin")}>Administration</button>}
            </nav>
            <footer>
                <strong>{session.user.name}</strong>
                <small>{session.user.email} · {session.actor.role}</small>
                <button className="link" onClick={() => void logout()}>Sign out</button>
            </footer>
        </aside>
        <main className="content">
            {error && <p className="error banner" role="alert">{error}<button onClick={() => onError("")}>×</button></p>}
            {section === "artifacts" ? <Artifacts onError={onError} /> : <Admin onError={onError} />}
        </main>
    </div>;
}

export function Artifacts({ onError }: { onError: (value: string) => void }) {
    const artifactId = window.location.pathname.match(/^\/artifacts\/([0-9a-f-]{36})$/i)?.[1];
    return artifactId
        ? <ArtifactEditor artifactId={artifactId} onError={onError} />
        : <ArtifactOverview onError={onError} />;
}

function ArtifactOverview({ onError }: { onError: (value: string) => void }) {
    const [artifacts, setArtifacts] = useState<ArtifactSummary[]>([]);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        void api<{ artifacts: ArtifactSummary[] }>("/shell-api/artifacts")
            .then((list) => { if (!cancelled) setArtifacts(list.artifacts); })
            .catch((error: unknown) => {
                if (!cancelled) { setFailed(true); onError(message(error)); }
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [onError]);

    const filtered = artifacts.filter((artifact) => artifact.title.toLocaleLowerCase()
        .includes(query.trim().toLocaleLowerCase()));

    return <section className="artifact-overview" aria-labelledby="artifacts-heading">
        <header className="overview-header">
            <div><h1 id="artifacts-heading">Artifacts</h1>
                <p className="muted">Your documents, all in one place. Select an artifact to open it.</p></div>
            <label className="artifact-search">Search artifacts
                <input type="search" placeholder="Search by title…" value={query}
                    onChange={(event) => setQuery(event.target.value)} />
            </label>
        </header>
        <div className="overview-summary">
            <h2>Available <span>{artifacts.length}</span></h2>
            {query.trim() && <p className="muted" role="status">{filtered.length} results</p>}
        </div>
        {loading ? <p className="overview-empty muted" role="status">Loading artifacts…</p>
            : failed ? <p className="overview-empty muted">Artifacts could not be loaded. Reload to try again.</p>
                : filtered.length === 0 ? <div className="panel overview-empty">
                    <h3>{artifacts.length === 0 ? "No artifacts yet" : "No matching artifacts"}</h3>
                    <p className="muted">{artifacts.length === 0
                        ? "Documents shared with you will appear here." : "Try another title or clear your search."}</p>
                    {query && <button onClick={() => setQuery("")}>Clear search</button>}
                </div> : <ul className="artifact-cards">
                    {filtered.map((artifact) => <li key={artifact.id}>
                        <a className="artifact-card" href={"/view/" + artifact.id}>
                            <span className="artifact-card-top"><span className="artifact-kind">
                                {artifact.kind === "designed" ? "Document" : "HTML"}</span>
                                <span className="artifact-arrow" aria-hidden="true">→</span></span>
                            <h3>{artifact.title}</h3>
                            <div className="artifact-meta"><span>Version {artifact.currentVersion}</span>
                                <span>{artifact.permission === "write" ? "Can edit" : "Read only"}</span></div>
                            <time dateTime={artifact.updatedAt}>Updated {new Date(artifact.updatedAt).toLocaleDateString(
                                undefined, { day: "numeric", month: "short", year: "numeric" },
                            )}</time>
                        </a>
                    </li>)}
                </ul>}
    </section>;
}

function ArtifactEditor({ artifactId, onError }: { artifactId: string; onError: (value: string) => void }) {
    const [selected, setSelected] = useState<Artifact>();
    const [title, setTitle] = useState("");
    const [html, setHtml] = useState("");
    const [frameUrl, setFrameUrl] = useState("");
    const [sharePrincipal, setSharePrincipal] = useState("");

    // Ignore in-flight loads after unmount or when a newer load supersedes them.
    const openSequence = useRef(0);

    const open = useCallback(async (id: string) => {
        const sequence = ++openSequence.current;
        try {
            const artifact = await api<Artifact>(`/shell-api/artifacts/${id}`);
            if (sequence !== openSequence.current) return;
            setSelected(artifact);
            setTitle(artifact.title);
            setHtml(artifact.html);
            // A designed artifact stores a document model, not markup. The sandbox preview would
            // render that JSON verbatim, and saving it back is refused as a kind mismatch anyway --
            // so the workspace offers neither and points at /view/<id>, which renders it properly.
            if (artifact.kind === "designed") {
                setFrameUrl("");
                return;
            }
            const render = await api<{ token: string }>(`/shell-api/artifacts/${id}/render-token`, { method: "POST" });
            if (sequence !== openSequence.current) return;
            // Same origin, addressed by path. What isolates the document is the sandbox CSP
            // directive on the response, not its location -- see sandboxHeaders() in app.ts.
            setFrameUrl(`/render/${encodeURIComponent(id)}?token=${encodeURIComponent(render.token)}`);
        } catch (requestError) {
            if (sequence === openSequence.current) onError(message(requestError));
        }
    }, [onError]);

    const save = async () => {
        if (!selected) return;
        try {
            const artifact = await api<Artifact>(`/shell-api/artifacts/${selected.id}`, {
                method: "PUT",
                ...body({ title, html, expectedVersion: selected.currentVersion }),
            });
            setSelected(artifact);
            await open(artifact.id);
        } catch (requestError) {
            onError(message(requestError));
        }
    };
    const remove = async () => {
        if (!selected || !window.confirm(`Delete “${selected.title}”?`)) return;
        try {
            await api(`/shell-api/artifacts/${selected.id}`, { method: "DELETE" });
            setSelected(undefined);
            setFrameUrl("");
            window.location.assign("/");
        } catch (requestError) {
            onError(message(requestError));
        }
    };
    const share = async (permission: Permission | null) => {
        if (!selected) return;
        try {
            await api(`/shell-api/artifacts/${selected.id}/share`, {
                method: "POST",
                ...body({ principalId: sharePrincipal, permission }),
            });
            setSharePrincipal("");
        } catch (requestError) {
            onError(message(requestError));
        }
    };

    useEffect(() => {
        const sequence = openSequence;
        void open(artifactId);
        return () => { sequence.current++; };
    }, [artifactId, open]);

    if (!selected) return <p className="muted" role="status">Loading artifact…</p>;

    const isDesigned = selected.kind === "designed";

    return <>
        <header><div><a className="link" href="/">&larr; All artifacts</a>
            <h1>{selected.title}</h1></div></header>
        <div className="artifact-grid">
            <section className="panel editor">
                <div className="row"><h2>Edit artifact</h2>{selected && <code>v{selected.currentVersion}</code>}</div>
                {isDesigned && <p className="muted">
                    A designed document. Its content is a structured model revised through the MCP
                    tools, so it is shown here read-only.
                </p>}
                <label>Title<input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} disabled={isDesigned} /></label>
                <label>{isDesigned ? "Document model (read-only)" : "HTML"}<textarea value={html} onChange={(event) => setHtml(event.target.value)} spellCheck={false} readOnly={isDesigned} /></label>
                <div className="actions">
                    <button className="primary" onClick={() => void save()} disabled={isDesigned}>Save new version</button>
                    {selected && <button className="danger" onClick={() => void remove()}>Delete</button>}
                </div>
                {selected && <div className="share-box">
                    <h3>Share by user ID</h3>
                    <input value={sharePrincipal} onChange={(event) => setSharePrincipal(event.target.value)} placeholder="User ID" />
                    <div className="actions">
                        <button onClick={() => void share("read")}>Grant read</button>
                        <button onClick={() => void share("write")}>Grant write</button>
                        <button onClick={() => void share(null)}>Revoke</button>
                    </div>
                </div>}
            </section>
            <section className="panel preview">
                {/*
                  * The preview here is a working surface: it sits beside the editor and is torn
                  * down the moment another artifact is picked. `/view/<id>` is the same document
                  * on a page of its own -- stable enough to paste into a chat, and the only form
                  * a colleague can be sent. It still grants nothing: the reader needs a session
                  * and a grant, so the link is a pointer, not a key.
                  */}
                <div className="row">
                    <h2>Sandbox preview</h2>
                    {selected && <a className="link" href={`/view/${selected.id}`}>Open shareable view &rarr;</a>}
                </div>
                {frameUrl
                    ? <iframe title="Artifact preview" src={frameUrl} sandbox="allow-scripts allow-forms" referrerPolicy="no-referrer" />
                    : isDesigned && selected
                        ? <p className="muted">Designed documents are rendered on their own page. <a className="link" href={`/view/${selected.id}`}>Open it &rarr;</a></p>
                        : <p className="muted">Select an artifact to issue a one-time render token.</p>}
            </section>
        </div>
    </>;
}

function Admin({ onError }: { onError: (value: string) => void }) {
    const [tab, setTab] = useState<"users" | "clients" | "audit" | "status">("users");
    return <>
        <header><div><h1>Administration</h1><p className="muted">Identity, MCP availability and audit controls</p></div></header>
        <div className="tabs">
            {(["users", "clients", "audit", "status"] as const).map((item) =>
                <button className={tab === item ? "active" : ""} key={item} onClick={() => setTab(item)}>{item}</button>)}
        </div>
        {tab === "users" && <Users onError={onError} />}
        {tab === "clients" && <Clients onError={onError} />}
        {tab === "audit" && <Audit onError={onError} />}
        {tab === "status" && <Status onError={onError} />}
    </>;
}

function Users({ onError }: { onError: (value: string) => void }) {
    const [users, setUsers] = useState<ManagedUser[]>([]);
    const [draft, setDraft] = useState({ name: "", email: "", password: "", role: "user" as Role });
    const [passwords, setPasswords] = useState<Record<string, string>>({});
    const load = useCallback(async () => {
        try { setUsers((await api<{ users: ManagedUser[] }>("/shell-api/admin/users")).users); }
        catch (requestError) { onError(message(requestError)); }
    }, [onError]);
    useEffect(() => void load(), [load]);
    const create = async (event: FormEvent) => {
        event.preventDefault();
        try {
            await api("/shell-api/admin/users", { method: "POST", ...body(draft) });
            setDraft({ name: "", email: "", password: "", role: "user" });
            await load();
        } catch (requestError) { onError(message(requestError)); }
    };
    const patchUser = (id: string, patch: Partial<ManagedUser>) =>
        setUsers((current) => current.map((user) => user.id === id ? { ...user, ...patch } : user));
    const save = async (user: ManagedUser) => {
        try {
            await api(`/shell-api/admin/users/${user.id}`, {
                method: "PATCH",
                ...body({ role: user.role, banned: user.banned, mcpEnabled: user.mcpEnabled, mcpScopes: user.mcpScopes }),
            });
            await load();
        } catch (requestError) { onError(message(requestError)); }
    };
    const action = async (path: string, method = "POST", value?: unknown) => {
        try { await api(path, { method, ...(value ? body(value) : {}) }); await load(); }
        catch (requestError) { onError(message(requestError)); }
    };
    return <div className="admin-stack">
        <form className="panel create-user" onSubmit={create}>
            <h2>Create user</h2>
            <input placeholder="Name" value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} required />
            <input type="email" placeholder="Email" value={draft.email} onChange={(event) => setDraft({ ...draft, email: event.target.value })} required />
            <input type="password" minLength={12} placeholder="Temporary password (12+ characters)" value={draft.password}
                onChange={(event) => setDraft({ ...draft, password: event.target.value })} required />
            <select value={draft.role} onChange={(event) => setDraft({ ...draft, role: event.target.value as Role })}>
                <option value="user">User</option><option value="admin">Admin</option>
            </select>
            <button className="primary">Create</button>
        </form>
        <section className="panel table-wrap">
            <table><thead><tr><th>User</th><th>Role</th><th>Status</th><th>MCP</th><th>Scopes</th><th>Actions</th></tr></thead>
                <tbody>{users.map((user) => <tr key={user.id}>
                    <td><strong>{user.name}</strong><small>{user.email}<br /><code>{user.id}</code></small></td>
                    <td><select value={user.role} onChange={(event) => patchUser(user.id, { role: event.target.value as Role })}>
                        <option value="user">User</option><option value="admin">Admin</option></select></td>
                    <td><label className="check"><input type="checkbox" checked={user.banned}
                        onChange={(event) => patchUser(user.id, { banned: event.target.checked })} /> Disabled</label></td>
                    <td><label className="check"><input type="checkbox" checked={user.mcpEnabled}
                        onChange={(event) => patchUser(user.id, { mcpEnabled: event.target.checked })} /> Enabled</label></td>
                    <td>{(["artifacts:read", "artifacts:write", "comments:write"] as Scope[]).map((scope) => <label className="check" key={scope}>
                        <input type="checkbox" checked={user.mcpScopes.includes(scope)} onChange={(event) => patchUser(user.id, {
                            mcpScopes: event.target.checked ? [...user.mcpScopes, scope] : user.mcpScopes.filter((item) => item !== scope),
                        })} /> {scope.replace("artifacts:", "")}</label>)}</td>
                    <td className="actions-cell"><button onClick={() => void save(user)}>Save</button>
                        <button onClick={() => void action(`/shell-api/admin/users/${user.id}/revoke-sessions`)}>Revoke sessions</button>
                        <div className="inline"><input type="password" placeholder="New password" value={passwords[user.id] ?? ""}
                            onChange={(event) => setPasswords({ ...passwords, [user.id]: event.target.value })} />
                        <button onClick={() => void action(`/shell-api/admin/users/${user.id}/password`, "POST", { password: passwords[user.id] })}>Set</button></div>
                        <button className="danger" onClick={() => {
                            if (window.confirm(`Delete ${user.email}?`)) void action(`/shell-api/admin/users/${user.id}`, "DELETE");
                        }}>Delete</button></td>
                </tr>)}</tbody></table>
        </section>
    </div>;
}

function Clients({ onError }: { onError: (value: string) => void }) {
    const [clients, setClients] = useState<OAuthClient[]>([]);
    const [name, setName] = useState("");
    const [redirectUri, setRedirectUri] = useState("");
    const [createdId, setCreatedId] = useState("");
    const [commentScope, setCommentScope] = useState(false);
    const load = useCallback(async () => {
        try { setClients((await api<{ clients: OAuthClient[] }>("/shell-api/admin/oauth-clients")).clients); }
        catch (requestError) { onError(message(requestError)); }
    }, [onError]);
    useEffect(() => void load(), [load]);
    const create = async (event: FormEvent) => {
        event.preventDefault();
        try {
            const result = await api<{ client_id: string }>("/shell-api/admin/oauth-clients", {
                method: "POST",
                ...body({ name, redirectUris: [redirectUri], scopes: ["artifacts:read", "artifacts:write", ...(commentScope ? ["comments:write"] : [])] }),
            });
            setCreatedId(result.client_id);
            setName(""); setRedirectUri(""); await load();
        } catch (requestError) { onError(message(requestError)); }
    };
    const saveScopes = async (client: OAuthClient) => {
        try {
            await api(`/shell-api/admin/oauth-clients/${client.clientId}`, {
                method: "PATCH",
                ...body({ disabled: client.disabled, redirectUris: client.redirectUris, scopes: client.scopes }),
            });
            await load();
        } catch (requestError) { onError(message(requestError)); }
    };
    const revoke = async (clientId: string) => {
        try { await api(`/shell-api/admin/oauth-clients/${clientId}`, { method: "DELETE" }); await load(); }
        catch (requestError) { onError(message(requestError)); }
    };
    return <div className="admin-stack">
        <form className="panel create-user" onSubmit={create}><h2>Register MCP/OAuth client</h2>
            <input placeholder="Client name" value={name} onChange={(event) => setName(event.target.value)} required />
            <input type="url" placeholder="Redirect URI" value={redirectUri} onChange={(event) => setRedirectUri(event.target.value)} required />
            <label className="check"><input type="checkbox" checked={commentScope}
                onChange={(event) => setCommentScope(event.target.checked)} /> comments:write</label>
            <button className="primary">Register public PKCE client</button>
            {createdId && <p>Client ID: <code>{createdId}</code></p>}
        </form>
        <section className="panel table-wrap"><table><thead><tr><th>Name</th><th>Client ID</th><th>Redirects</th><th>Scopes</th><th>Status</th><th /></tr></thead>
            <tbody>{clients.map((client) => <tr key={client.clientId}><td>{client.name}</td><td><code>{client.clientId}</code></td>
                <td>{client.redirectUris.join(", ")}</td><td>{(["artifacts:read", "artifacts:write", "comments:write"] as Scope[]).map((scope) =>
                    <label className="check" key={scope}><input type="checkbox" checked={client.scopes.includes(scope)}
                        onChange={(event) => setClients(clients.map((entry) => entry.clientId === client.clientId
                            ? { ...entry, scopes: event.target.checked ? [...entry.scopes, scope] : entry.scopes.filter((item) => item !== scope) }
                            : entry))} /> {scope}</label>)}
                    <button disabled={!client.scopes.length} onClick={() => void saveScopes(client)}>Save scopes</button>
                </td><td>{client.disabled ? "Revoked" : "Active"}</td>
                <td><button className="danger" disabled={client.disabled} onClick={() => void revoke(client.clientId)}>Revoke</button></td></tr>)}</tbody></table></section>
    </div>;
}

function Audit({ onError }: { onError: (value: string) => void }) {
    const [events, setEvents] = useState<AuditEvent[]>([]);
    const [query, setQuery] = useState("");
    const load = useCallback(async () => {
        try { setEvents((await api<{ events: AuditEvent[] }>(`/shell-api/admin/audit?q=${encodeURIComponent(query)}`)).events); }
        catch (requestError) { onError(message(requestError)); }
    }, [onError, query]);
    useEffect(() => void load(), [load]);
    return <section className="panel table-wrap"><div className="row"><h2>Audit log</h2>
        <input placeholder="Search actor or action" value={query} onChange={(event) => setQuery(event.target.value)} /></div>
        <table><thead><tr><th>Time</th><th>Actor</th><th>Action</th><th>Target</th><th>Outcome</th></tr></thead>
            <tbody>{events.map((event) => <tr key={event.id}><td>{new Date(event.createdAt).toLocaleString()}</td>
                <td><code>{event.actorId ?? "anonymous"}</code></td><td>{event.action}</td>
                <td>{event.targetType}:{event.targetId ?? "-"}</td><td>{event.outcome}</td></tr>)}</tbody></table></section>;
}

function Status({ onError }: { onError: (value: string) => void }) {
    const [status, setStatus] = useState<Record<string, unknown>>();
    useEffect(() => {
        api<Record<string, unknown>>("/shell-api/admin/status").then(setStatus).catch((error: unknown) => onError(message(error)));
    }, [onError]);
    return <section className="panel"><h2>MCP availability</h2>{status ? <dl>{Object.entries(status).map(([key, value]) =>
        <div key={key}><dt>{key}</dt><dd><code>{Array.isArray(value) ? value.join(", ") : String(value)}</code></dd></div>)}</dl>
        : <p>Loading…</p>}</section>;
}

function message(error: unknown): string {
    return error instanceof Error ? error.message : "Unexpected error";
}
